import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { SignaturePad } from '../components/SignaturePad';
import { Handshake, PenTool, CheckCircle2, Download, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return iso; } };

export default function MoratoriumSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sigB64, setSigB64] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [padOpen, setPadOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/moratorium/${token}`);
        setData(data);
        setSigned(!!data.already_signed);
        setSignerName(data?.moratorium?.tenant_name || '');
      } catch (e) {
        setError(e?.response?.data?.detail || 'Lien introuvable ou expiré');
      } finally { setLoading(false); }
    })();
  }, [token]);

  const handleSign = async () => {
    if (!sigB64) return toast.error('Veuillez signer');
    if ((signerName || '').trim().length < 3) return toast.error('Nom requis (3 car. min)');
    if (!accepted) return toast.error("Veuillez cocher 'Lu et approuvé'");
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/moratorium/${token}/sign`, {
        signature_base64: sigB64, signer_name: signerName.trim(), read_and_approved: true,
      });
      toast.success('Signature enregistrée !');
      setSigned(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSubmitting(false); }
  };

  const downloadPdf = () => window.open(`${API}/public/moratorium/${token}/pdf`, '_blank');

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FBF6EE]">Chargement…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBF6EE] p-6">
      <Card className="max-w-md border-red-200">
        <CardContent className="p-8 text-center">
          <p className="text-red-600 font-semibold">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  const m = data?.moratorium;
  const agency = data?.agency || {};

  return (
    <div className="min-h-screen bg-[#FBF6EE] py-8 px-4" data-testid="moratorium-sign-page">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#B85C38]/10 mb-3">
            <Handshake className="w-7 h-7 text-[#B85C38]" />
          </div>
          <h1 className="text-2xl font-bold text-[#2B3A2C]">Protocole d'accord de moratoire</h1>
          <p className="text-sm text-slate-600 mt-1">{agency.name}</p>
        </div>

        <Card className="border-[#E8D9C0]">
          <CardHeader>
            <CardTitle className="text-base text-[#2B3A2C]">Récapitulatif</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Débiteur</span><b>{m.tenant_name}</b></div>
            <div className="flex justify-between"><span className="text-slate-500">Bien</span><b>{m.property_title}</b></div>
            <div className="flex justify-between"><span className="text-slate-500">Montant total</span><b className="text-[#B85C38]">{fmt(m.total_amount)} XOF</b></div>
            <div className="flex justify-between"><span className="text-slate-500">Nombre d'échéances</span><b>{m.number_of_installments}</b></div>
            {m.reason && <div className="flex justify-between"><span className="text-slate-500">Motif</span><b className="max-w-[60%] text-right">{m.reason}</b></div>}
          </CardContent>
        </Card>

        <Card className="border-[#E8D9C0]">
          <CardHeader><CardTitle className="text-base text-[#2B3A2C] flex items-center gap-2"><Calendar className="w-4 h-4" /> Échéancier</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(m.installments || []).map((inst) => (
                <div key={inst.index} className="flex items-center justify-between p-2 rounded-lg bg-white border">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#2B3A2C] text-white text-xs flex items-center justify-center">{inst.index}</span>
                    <span className="text-sm">{fmtDate(inst.due_date)}</span>
                  </div>
                  <b className="text-sm text-[#B85C38]">{fmt(inst.amount)} XOF</b>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {signed ? (
          <Card className="border-emerald-300 bg-emerald-50" data-testid="moratorium-signed-success">
            <CardContent className="p-6 text-center space-y-3">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
              <p className="font-semibold text-emerald-900">Protocole signé avec succès</p>
              <p className="text-sm text-slate-600">Vous pouvez télécharger votre exemplaire signé.</p>
              <Button onClick={downloadPdf} className="bg-[#2B3A2C] hover:bg-[#1e2a20] text-white rounded-full" data-testid="moratorium-download-signed-btn">
                <Download className="w-4 h-4 mr-2" /> Télécharger le PDF signé
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-[#E8D9C0]">
            <CardHeader><CardTitle className="text-base text-[#2B3A2C]">Signer électroniquement</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Nom complet du signataire</Label>
                <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} className="mt-1" data-testid="moratorium-signer-name" />
              </div>
              {padOpen ? (
                <SignaturePad width={480} height={160} onSave={(b64) => { setSigB64(b64); setPadOpen(false); }} onCancel={() => setPadOpen(false)} />
              ) : sigB64 ? (
                <div className="p-2 border rounded bg-white">
                  <img src={sigB64} alt="signature" className="max-h-20 mx-auto" />
                  <Button variant="outline" size="sm" onClick={() => setPadOpen(true)} className="mt-2 w-full" data-testid="moratorium-resign-btn">Re-signer</Button>
                </div>
              ) : (
                <Button onClick={() => setPadOpen(true)} className="w-full bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-full" data-testid="moratorium-open-pad-btn">
                  <PenTool className="w-4 h-4 mr-2" /> Ouvrir la zone de signature
                </Button>
              )}
              <div className="flex items-start gap-2 bg-[#FBF6EE] p-3 rounded-lg">
                <Checkbox id="mor-accept" checked={accepted} onCheckedChange={setAccepted} data-testid="moratorium-accept-checkbox" />
                <label htmlFor="mor-accept" className="text-sm cursor-pointer">
                  J'ai lu et j'approuve le présent protocole, et je m'engage à respecter l'échéancier ci-dessus.
                </label>
              </div>
              <Button onClick={handleSign} disabled={submitting || !sigB64 || !accepted || (signerName || '').trim().length < 3}
                className="w-full bg-[#2B3A2C] hover:bg-[#1e2a20] text-white rounded-full py-6 disabled:opacity-50"
                data-testid="moratorium-submit-btn">
                {submitting ? 'Enregistrement…' : 'Signer et valider'}
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="text-center text-xs text-slate-400">
          Document généré par {agency.name || "GAB'S SERVICES HABITAT"}
        </div>
      </div>
    </div>
  );
}
